import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/redux/redux-store/hooks';
import {
  BackgroundImage,
  Image,
  LoadingOverlay,
  Tabs,
  Text,
} from '@mantine/core';
import {
  setSelectedChild,
  setSelectedSession,
  setChildMessages,
} from 'src/redux/redux-store/children/childrenReducer';
import { StyledContainer } from 'src/components/common/StyledComponents/Container';
import { UserRole } from 'src/enums/User';
import { Navigate, useNavigate } from 'react-router-dom';
import { getRole } from 'src/utils/storage';
import {
  handleUpdateAppSettings,
  handleUserUpdate,
} from 'src/redux/redux-store';
import { ChatStyledContainerInset } from 'src/components/common/StyledComponents/BackgroundContainer';
import { Notifications } from '@mantine/notifications';
import useResponsive from 'src/hooks/useResponsive';
import DynamicLayout from 'src/components/Layout/DynamicLayout';
import ParentChildViewSidebar from 'src/components/Sidebars/Parent/ParentSidebarChildView';
import useChildrenData from 'src/hooks/useChildrenData';
import ReadOnlyChat from 'src/components/ReadOnlyChat';
import { FlaggedData } from 'src/interfaces/Chat';
import Icon from 'src/components/common/Icons/Icons/Icon';
import SubjectDropdown from 'src/components/Chat/SubjectDropdown';

const ChildView = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.user);
  const { backgroundColor, backgroundColorExterior, isMobile, isDesktop } =
    useResponsive();
  const [isLoading, setIsLoading] = useState(false);

  const useChildrenDataResult = useChildrenData();
  const {
    childMessages,
    selectedChild,
    flagMessage,
    getChildMessages,
    selectedSession,
    childSessions,
    setActiveSubject,
    activeSubject,
    subjects: storeSubjects,
  } = useChildrenDataResult;

  // Get subjects from the store
  const subjects = useMemo(() => {
    if (!storeSubjects || storeSubjects.length === 0) return [];
    // Ensure we only work with strings
    return storeSubjects.map((subject) =>
      typeof subject === 'string' ? subject : subject.name
    );
  }, [storeSubjects]);



  // Auto-select the first subject when subjects are loaded and no subject is selected
  useEffect(() => {
    if (subjects.length > 0 && !activeSubject) {
      setActiveSubject(subjects[0]); // Select first subject
    }
  }, [subjects, activeSubject, setActiveSubject]);

  console.log('Subjects in ChildView:', subjects, 'Active:', activeSubject);

  // Filter sessions based on active subject
  const filteredSessions = useMemo(() => {
    if (!activeSubject || activeSubject === 'All') {
      return childSessions;
    }
    return childSessions.filter((session) => session.subject === activeSubject);
  }, [childSessions, activeSubject]);

  const handleSubjectChange = useCallback(
    (subject: string) => {
      const newSubject = subject === 'All' ? null : subject;
      setActiveSubject(newSubject);

      // If we have sessions for this subject, select the first one
      if (newSubject && newSubject !== 'All') {
        const firstSession = childSessions.find(
          (s) => s.subject === newSubject
        );
        if (firstSession) {
          dispatch(setSelectedSession(firstSession.id));
        } else {
          dispatch(setSelectedSession(''));
          dispatch(setChildMessages([]));
        }
      } else {
        // If 'All' is selected or no subject, clear the selection
        dispatch(setSelectedSession(''));
        dispatch(setChildMessages([]));
      }
    },
    [childSessions, dispatch, setActiveSubject]
  );

  useEffect(() => {
    if (selectedSession) {
      setIsLoading(true);
      getChildMessages(selectedChild, selectedSession).finally(() => {
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  }, [selectedSession]);

  const header = selectedChild ? (
    <div className='flex flex-row items-center justify-between gap-2 w-full'>
      <div className='flex gap-2 min-w-max'>
        <Icon name='chat' className='size-6 md:size-8' />
        <Text className='text-lg font-semibold'>{selectedChild}</Text>
      </div>
      {subjects.length > 0 && (
        <div className='ml-2'>
          <SubjectDropdown
            subjects={subjects}
            activeSubject={
              activeSubject || (subjects.length > 0 ? subjects[0] : '')
            }
            onSubjectChange={handleSubjectChange}
          />
        </div>
      )}
    </div>
  ) : (
    <Text className='text-lg font-semibold'>Child View</Text>
  );

  const sidebarProps = useMemo(
    () => ({
      selectedSession,
      childSessions: filteredSessions,
      onSelectSession: (id: string) => {
        dispatch(setSelectedSession(id));
      },
      children: [], // Pass empty array as we're not using it in the sidebar
      selectedChild,
      onSelectChild: (username: string) => {
        dispatch(setSelectedChild(username));
        dispatch(setSelectedSession(''));
        dispatch(setChildMessages([]));
      },
    }),
    [selectedSession, filteredSessions, selectedChild, dispatch]
  );

  // Create a custom sidebar component that wraps ParentChildViewSidebar with the required props
  const CustomSidebar = React.memo(() => {
    // Use the sidebarProps directly since ParentChildViewSidebar can handle both internal and external state
    return <ParentChildViewSidebar />;
  });

  // Ensure the component updates when relevant props change
  CustomSidebar.displayName = 'CustomSidebar';

  return (
    <DynamicLayout
      heading={header}
      rightContent={
        <>
          <SubjectDropdown
            subjects={subjects}
            activeSubject={
              activeSubject || (subjects.length > 0 ? subjects[0] : '')
            }
            onSubjectChange={handleSubjectChange}
          />
        </>
      }
      SidebarComponent={CustomSidebar}
    >
      <StyledContainer theme={{ backgroundColor: backgroundColorExterior }}>
        <ChatStyledContainerInset theme={{ backgroundColor: backgroundColor }}>
          <BackgroundImage className='' src='/images/doodles.png'>
            <Notifications
              position={isMobile ? 'top-center' : 'bottom-right'}
              limit={isMobile ? 1 : 5}
            />
            {/* <ErrorMessage error={error ? error : ""} handleClose={dismissError} />  */}
            <LoadingOverlay visible={isLoading} overlayBlur={2} />
            <div className='flex h-[calc(100dvh-40px)] items-center justify-center'>
              {selectedChild ? (
                <div className='flex -mt-[20px] h-[calc(100dvh-50px)] lg:-mt-[35px] lg:h-[calc(100dvh-70px)] flex-col w-full lg:w-[700px] xl:w-[85%] mx-auto px-4 lg:px-0 overflow-hidden relative'>
                  {/* Always show subject tabs when a child is selected */}
                  {/* Show either the chat or the empty state */}
                  <div className='flex-1 overflow-hidden'>
                    {selectedSession || activeSubject === 'All' ? (
                      <ReadOnlyChat
                        childUsername={selectedChild}
                        onFlagMessage={flagMessage}
                        messages={childMessages}
                        subjects={[]} // We're handling tabs outside now
                        activeSubject={activeSubject || 'All'}
                        onSubjectChange={() => { }} // No-op since we're handling it above
                      />
                    ) : (
                      <div className='h-full flex items-center justify-center'>
                        <div className='text-center p-6 bg-white rounded-lg shadow-sm'>
                          <Image
                            src={'/landing/logo.png'}
                            className='mx-auto mb-4 max-w-[60dvw] sm:max-w-56'
                            alt='Select a session'
                          />
                          <Text className='text-base text-gray-400 font-semibold'>
                            {filteredSessions.length === 0
                              ? 'No chat sessions available for this subject'
                              : 'Select a session from the sidebar to view messages'}
                          </Text>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  className='flex flex-col items-center justify-center gap-4 w-full'
                // style={{ height: heightStyle }}
                >
                  <Image
                    src={'/landing/logo.png'}
                    className='self-center max-w-[60dvw] sm:max-w-56'
                  />
                  <Text className='text-base text-gray-400 font-semibold'>
                    Please select a child from the sidebar
                  </Text>
                </div>
              )}
            </div>
          </BackgroundImage>
        </ChatStyledContainerInset>
      </StyledContainer>
    </DynamicLayout>
  );
};

export default ChildView;
